import React from 'react';

import { Button, Popconfirm, Space } from 'antd';
import { useTranslation } from 'react-i18next';

import { NSKeys } from '@app/i18n';
import { IError } from '@app/models/error';
import { RetrieveWarehouseSchema } from '@app/models/wms';

import { useDeactivateWarehouseQuery, useReactivateWarehouseQuery } from '../../hooks';
import { SwitchedOffCatalogsModal } from '../SwitchedOffCatalogsModal';
import { UnfinishedOrdersModal } from '../UnfinishedOrdersModal';

interface IProps {
  warehouse: RetrieveWarehouseSchema;
}

export const WarehouseActions: React.FC<IProps> = ({ warehouse }) => {
  const warehousesT = useTranslation(NSKeys.warehouses);
  const commonT = useTranslation(NSKeys.common);
  const [unfinishedOrders, setUnfinishedOrders] = React.useState<IError>();
  const [switchedOff, setSwitchedOff] = React.useState<string[]>();
  const deactivate = useDeactivateWarehouseQuery(setUnfinishedOrders);
  const reactivate = useReactivateWarehouseQuery();
  const isDeactivated = !!warehouse.deactivated_at;
  const canDeactivate = !!warehouse.$permissions.deactivate && !isDeactivated;
  const canReactivate = !!warehouse.$permissions.reactivate && isDeactivated;

  if (!canDeactivate && !canReactivate && !switchedOff && !unfinishedOrders) {
    return null;
  }

  return (
    <Space size={0} onClick={(event) => event.stopPropagation()}>
      {canDeactivate && (
        <Popconfirm
          title={warehousesT.t('deactivateConfirmTitle')}
          description={warehousesT.t('deactivateConfirmDescription')}
          okText={commonT.t('confirm')}
          okButtonProps={{ danger: true }}
          cancelText={commonT.t('cancel')}
          onConfirm={() => deactivate.mutate(warehouse.uid, { onSuccess: () => setSwitchedOff([warehouse.uid]) })}
        >
          <Button type="link" size="small" danger loading={deactivate.isLoading}>
            {warehousesT.t('deactivate')}
          </Button>
        </Popconfirm>
      )}
      {canReactivate && (
        <Button
          type="link"
          size="small"
          loading={reactivate.isLoading}
          onClick={() => reactivate.mutate(warehouse.uid)}
        >
          {warehousesT.t('reactivate')}
        </Button>
      )}
      <UnfinishedOrdersModal error={unfinishedOrders} onClose={() => setUnfinishedOrders(undefined)} />
      <SwitchedOffCatalogsModal warehouseUids={switchedOff} onClose={() => setSwitchedOff(undefined)} />
    </Space>
  );
};
